import { Injectable } from '@angular/core';
import { AgTranslateService } from '@ag/vc.ag-core/translate';
import { GlobalConfig, ToastrService } from 'ngx-toastr';
import { PunctuationWrapPipe } from '../pipes/punctuation-wrap.pipe';
import { Subject } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { CZFlow } from '../models/PersonAndPreferences';

export enum ToastrType {
  success = 1,
  error = 2,
  warning = 3,
  info = 4
}

@Injectable({
  providedIn: 'root'
})
export class SilToastrService {
  options: GlobalConfig;
  public toastLinkClicks = new Subject<CZFlow>();

  constructor(
    private toastr: ToastrService,
    private translateService: AgTranslateService,
    private punctuationWrap : PunctuationWrapPipe,
    private sanitizer: DomSanitizer
  ) {
    this.translateService.onLangChange.subscribe(() => {
      this.changeToastrContent();
    });
    this.options = this.toastr.toastrConfig;
  }

  public showToast(key: string, type: ToastrType): void {
    this.toastr.overlayContainer = undefined;
    this.translateService.get(key).subscribe((res: string) => {
      const punctuationWrap = this.punctuationWrap.transform(res);
      this.toastr.show(punctuationWrap as string, key, this.getToastConfig(type), ToastrType[type]);
    });
  }
  public showToastWithLink(key: string, type: ToastrType, toastId?:CZFlow): void {
    this.toastr.overlayContainer = undefined;
    this.translateService.get(key).subscribe((res: string) => {
      const punctuationWrap = this.punctuationWrap.transform(res);
      const sanitizedMessage = this.sanitizer.bypassSecurityTrustHtml(punctuationWrap as string);
      const toastRef = this.toastr.show(sanitizedMessage as string, key, this.getToastWithLinkConfig(type), ToastrType[type]);
      toastRef.onShown.subscribe(() => {
        this.addToastLinkClickListener(toastRef, toastId);
      })
    });
  }

  private addToastLinkClickListener(toastRef: any, toastId: CZFlow): void {
    setTimeout(() => {
      const toastElement = document.querySelector(`#toast-container .toast-with-link`);
      if(toastElement){
        toastElement.addEventListener('click', (event) => {
          const target = event.target as HTMLElement;
          if(target.closest('.toast-link')) {
            event.preventDefault();
            this.toastLinkClicks.next(toastId);
          }
        })
      }
    }, 0);
  }

  public showToastwithDynamicValue(key: string, type: ToastrType,value:{[key: string]: string}): void {
    this.toastr.overlayContainer = undefined;
    this.translateService.get(key,value).subscribe((res: string) => {
      this.toastr.show(res, key, this.getToastConfig(type), ToastrType[type]);
    });
  }

  public showInlineToast(key: string, type: ToastrType): void {
    this.translateService.get(key).subscribe((res: string) => {
      this.toastr.show(res, key, this.getInlineToastConfig(type), ToastrType[type]);
    });
  }

  public clearInlineToasts(): void {
    for (const toast of this.toastr.toasts) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      if (toast.toastRef.componentInstance.toastClasses.indexOf('inline-toastr') !== -1) {
        this.toastr.clear(toast.toastId);
      }
    }
  }

  public clearToastWithLink(): void {
    for (const toast of this.toastr.toasts) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      if (toast.toastRef.componentInstance.toastClasses.includes('toast-with-link')) {
        this.toastr.clear(toast.toastId);
      }
    }
  }

  public clearAllToasts(): void {
    this.toastr.clear();
  }

  private getToastWithLinkConfig(type: ToastrType): GlobalConfig {
    const customClass = this.getTypeClass(type, false);
    this.options.timeOut = 10000;
    this.options.extendedTimeOut = 10000;
    this.options.enableHtml = true;
    this.options.tapToDismiss = false;
    this.options.toastClass = `ngx-toastr global-toast toast-with-link ${customClass}`;
    this.options.closeButton = true;
    this.options.positionClass = 'toast-bottom-left';
    this.options.easeTime = 50;
    this.options.easing = 'cubic-bezier(0.3, 0, 0.6, 1)';
    this.options.preventDuplicates = true;
    this.options.progressBar = true;

    return this.options;
  }

  private getToastConfig(type: ToastrType): GlobalConfig {
    const customClass = this.getTypeClass(type, false);
    this.options.timeOut = 10000;
    this.options.extendedTimeOut = 10000;
    this.options.enableHtml = true;
    this.options.tapToDismiss = false;
    this.options.toastClass = `ngx-toastr global-toast ${customClass}`;
    this.options.closeButton = true;
    this.options.positionClass = 'toast-bottom-left';
    this.options.easeTime = 500;
    this.options.easing = 'cubic-bezier(0.3, 0, 0.6, 1)';
    this.options.preventDuplicates = true;
    this.options.progressBar = true;

    return this.options;
  }

  private getInlineToastConfig(type: ToastrType): GlobalConfig {
    const customClass = this.getTypeClass(type, true);
    this.options.timeOut = 0;
    this.options.extendedTimeOut = 0;
    this.options.enableHtml = true;
    this.options.tapToDismiss = false;
    this.options.toastClass = `ngx-toastr inline-toastr ${customClass}`;
    this.options.closeButton = false;
    this.options.positionClass = 'inline-toast';
    this.options.easeTime = 500;
    this.options.easing = 'cubic-bezier(0.3, 0, 0.6, 1)';
    this.options.preventDuplicates = false;

    return this.options;
  }

  private getTypeClass(type: ToastrType, isInline: boolean): string {
    const typeString = this.getType(type);
    return isInline ? `sil-inline-${typeString}-toastr` : `sil-${typeString}-toastr`;
  }

  private getType(type: ToastrType): string {
    switch (type) {
      case ToastrType.success:
        return 'success';
      case ToastrType.error:
        return 'error';
      case ToastrType.warning:
        return 'warning';
      default:
        return 'info';
    }
  }

  private changeToastrContent(): void {
    for (const toast of this.toastr.toasts) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const key = toast.toastRef.componentInstance.title;
      this.translateService.get(key as string | string[]).subscribe((res: string) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        toast.toastRef.componentInstance.message = res;
      });
    }
  }
}
